import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, withStyles } from '@material-ui/core';
import FillReviewFlow from '../../shared/fillReviewFlow';
import AlertDialog from '../../shared/alertDialog';
import { ReactComponent as SurveyExpiredIcon } from '../../../assets/icons/expired.svg';
import {
  QUESTIONNAIRE_QUESTION_ANSWER_TYPES,
  QUESTIONNAIRE_QUESTION_TYPES,
} from '../../../constants/survey';
import {
  asyncDebounce,
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  isValueInList,
} from '../../../utility/helpers';
import { getActiveReviewIndex } from '../../../utility/fillSurveyUtils';
import {
  prepareSurveySteps,
  AUTO_CHANGE_QUESTION_DELAY,
  setPageHeight,
} from './config';

const styles = ({ breakpoints, spacing }) => ({
  main: {
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    height: '100vh',
    fallbacks: [
      { height: '-moz-available' },
      { height: '-webkit-fill-available' },
      { height: 'fill-available' },
    ],
  },
  content: {
    boxSizing: 'border-box',
    margin: '0 auto',
    padding: spacing(4, 6, 0),
    width: '100%',
    maxWidth: 600,
    height: '100%',
    [breakpoints.up('sm')]: {
      width: 600,
      padding: spacing(4, 0, 6),
    },
    [breakpoints.up('lg')]: {
      width: 930,
      maxWidth: 930,
    },
  },
  generateSurvey: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  generateSurveyMessage: {
    textAlign: 'center',
  },
  surveyExpiredIcon: {
    marginBottom: spacing(6),
    width: 98,
    height: 98,
  },
});

class FillSurveyPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isSubmitOpened: false,
      isAddCommentDialogOpened: false,
      selectedSubject: {},
      feedbackErrors: {},
      answerReplyErrors: {},
      enpsReplyData: {},
    };
    this.handleFeedbackErrors = asyncDebounce(this.handleFeedbackErrors);
    this.handleSetComments = asyncDebounce(this.handleSetComments);
    this.setPageHeight = asyncDebounce(setPageHeight);
  }

  componentDidMount() {
    this.setPageHeight();
    window.addEventListener('resize', this.setPageHeight, false);
  }

  componentWillUnmount() {
    const { isInlineSurvey, clearFillSurvey } = this.props;

    clearFillSurvey(isInlineSurvey);
    window.removeEventListener('resize', this.setPageHeight, false);
  }

  handleStepChange = step => {
    const {
      trackingToken,
      fillSurvey,
      surveyStep,
      getSurveyReview,
      setReviewStep,
    } = this.props;

    if (step === -1) {
      return getSurveyReview(
        trackingToken,
        fillSurvey.attributes[surveyStep - 1].id
      );
    }

    return setReviewStep(step);
  };

  handleAnswerReplyErrors = (errorType, user) =>
    this.setState(prevState => ({
      answerReplyErrors: {
        ...prevState.answerReplyErrors,
        [user.review_tracking]: errorType,
      },
    }));

  handleFeedbackErrors = (errorType, userTracking) =>
    this.setState(prevState => ({
      feedbackErrors: {
        ...prevState.feedbackErrors,
        [userTracking]: errorType,
      },
    }));

  handleSetComments = comments => {
    const { setComments } = this.props;

    setComments(comments);
  };

  handleCloseInlineSurvey = () => {
    const { navigate, setIsInlineSurvey } = this.props;

    setIsInlineSurvey(false);

    navigate(-1);
  };

  handleFinishReview = () => {
    const {
      navigate,
      review,
      fillSurvey,
      surveyStep,
      submitReview,
      trackingToken,
      setAnswerTextResult,
    } = this.props;
    const { enpsReplyData } = this.state;

    const { isEnps } = review;
    if (isEnps) {
      const isReplyDirty = isObjectEmpty(enpsReplyData)
        ? true
        : enpsReplyData.answer.reply !==
          review?.results?.[enpsReplyData.review_tracking]?.[
            enpsReplyData.question
          ]?.reply;
      if (isReplyDirty) {
        setAnswerTextResult(trackingToken, enpsReplyData).then(() => {
          submitReview(trackingToken, [
            {
              review_tracking: enpsReplyData.review_tracking,
            },
          ]).then(() => {
            navigate('/thank-you');
          });
        });
      } else {
        submitReview(trackingToken, [
          {
            review_tracking: enpsReplyData.review_tracking,
          },
        ]).then(() => {
          navigate('/thank-you');
        });
      }
    } else {
      submitReview(trackingToken, review.comments).then(() => {
        if (surveyStep + 1 < fillSurvey.totalReviews) {
          this.handleGetReview(surveyStep + 1).then(
            this.toggleSubmitDialog,
            this.toggleSubmitDialog
          );
        } else {
          navigate('/thank-you');
        }
      });
    }
  };

  handleGetReview = (surveyStep = 0) => {
    const { fillSurvey, trackingToken, getSurveyReview } = this.props;

    return getSurveyReview(
      trackingToken,
      fillSurvey.questionnaire
        ? fillSurvey.questionnaire.id
        : fillSurvey.attributes[surveyStep]?.id,
      Boolean(fillSurvey.questionnaire)
    );
  };

  handleAttributeQuestionAnswerSelect = (
    questionId,
    answerId,
    user,
    hasAddCommentButton,
    shouldDeleteAnswer = false
  ) => {
    const {
      fillSurvey,
      review,
      reviewStep,
      trackingToken,
      setAttributeAnswerResult,
      setReviewStep,
    } = this.props;
    const { isEnps } = review;

    if (isEnps) {
      setAttributeAnswerResult(trackingToken, {
        review_tracking: user.review_tracking,
        answer: shouldDeleteAnswer ? null : answerId,
        question: questionId,
        subjectId: user.id,
      }).then(() => {
        if (review.totalSteps > reviewStep + 1) {
          setTimeout(() => {
            setReviewStep(reviewStep + 1);
          }, AUTO_CHANGE_QUESTION_DELAY);
        }
      });
    } else {
      setAttributeAnswerResult(trackingToken, {
        review_tracking: user.review_tracking,
        answer: shouldDeleteAnswer ? null : answerId,
        question: questionId,
        subjectId: user.id,
      }).then(() => {
        // switch to next question automatically
        if (
          fillSurvey.isSingleSurvey &&
          review.totalSteps > reviewStep + 1 &&
          !hasAddCommentButton
        ) {
          setTimeout(() => {
            setReviewStep(reviewStep + 1);
          }, AUTO_CHANGE_QUESTION_DELAY);
        }
      });
    }
  };

  handleAttributeQuestionAnswerChange = (questionId, user, answer) => {
    const { trackingToken, setAnswerTextResult, review } = this.props;
    const { isEnps } = review;

    if (isEnps) {
      this.setState({
        enpsReplyData: {
          review_tracking: user.review_tracking,
          answer,
          question: questionId,
        },
      });
    } else {
      return setAnswerTextResult(trackingToken, {
        review_tracking: user.review_tracking,
        answer,
        question: questionId,
      });
    }
  };

  handleQuestionnaireQuestionOptionSelect = (
    questionId,
    questionType,
    answerId,
    user,
    shouldDeleteAnswer = false
  ) => {
    const { trackingToken, setQuestionnaireAnswerResult, review } = this.props;
    const answerKey = QUESTIONNAIRE_QUESTION_ANSWER_TYPES[questionType];
    const currentIds =
      review.results?.[user.review_tracking]?.[questionId]?.[answerKey] ?? [];

    let selectedOptionIds;

    if (questionType === QUESTIONNAIRE_QUESTION_TYPES.MULTI_SELECT) {
      if (shouldDeleteAnswer) {
        selectedOptionIds = currentIds.filter(id => id !== answerId);
      } else if (isValueInList(currentIds, answerId)) {
        selectedOptionIds = currentIds;
      } else {
        selectedOptionIds = [...currentIds, answerId];
      }
    } else {
      selectedOptionIds = shouldDeleteAnswer ? [] : [answerId];
    }

    setQuestionnaireAnswerResult(trackingToken, {
      review_tracking: user.review_tracking,
      [answerKey]: selectedOptionIds,
      question: questionId,
      questionType,
    });
  };

  handleQuestionnaireQuestionAnswerChange = (
    questionId,
    questionType,
    answer,
    user
  ) => {
    const { trackingToken, setQuestionnaireAnswerResult } = this.props;

    return setQuestionnaireAnswerResult(trackingToken, {
      review_tracking: user.review_tracking,
      [QUESTIONNAIRE_QUESTION_ANSWER_TYPES[questionType]]: answer,
      question: questionId,
      questionType,
    });
  };

  toggleSubmitDialog = () =>
    this.setState(prevState => ({ isSubmitOpened: !prevState.isSubmitOpened }));

  handleSubmitReview = () => {
    const { surveyStep, review } = this.props;

    if (review.isSubmitted) {
      return this.handleGetReview(surveyStep + 1);
    }

    this.toggleSubmitDialog();
  };

  handleOpenAddCommentDialog = (subject = {}) => {
    this.setState(prevState => ({
      isAddCommentDialogOpened: !prevState.isAddCommentDialogOpened,
      selectedSubject: subject,
    }));
  };

  handleSaveComment = (questionId, comment) => {
    const { trackingToken, setAnswerCommentResult } = this.props;
    const { selectedSubject } = this.state;

    return setAnswerCommentResult(trackingToken, {
      review_tracking: selectedSubject.review_tracking,
      comment,
      question: questionId,
      subjectId: selectedSubject.id,
    });
  };

  renderExpiredSurvey = () => {
    const { classes, translations } = this.props;

    return (
      <Grid className={classes.generateSurvey} item xs={12}>
        <SurveyExpiredIcon className={classes.surveyExpiredIcon} />
        <Typography variant="h1" className={classes.generateSurveyMessage}>
          {translations.expiredSurveyMessage}
        </Typography>
      </Grid>
    );
  };

  render() {
    const {
      classes,
      translations,
      isSurveyLoaded,
      fillSurvey,
      review,
      surveyStep,
      reviewStep,
      isInlineSurvey,
    } = this.props;
    const {
      isSubmitOpened,
      feedbackErrors,
      answerReplyErrors,
      isAddCommentDialogOpened,
      selectedSubject,
    } = this.state;
    const isGroupReview =
      !fillSurvey?.isSingleSurvey &&
      isArray(review?.subjects) &&
      !isArrayEmpty(review?.subjects);

    const hasAddCommentButton =
      !review?.withTextReplyQuestions &&
      !isGroupReview &&
      review.hasAnswerComment;

    const activeReviewIndex = getActiveReviewIndex(fillSurvey.attributes);

    return (
      <Grid id="fill-survey-page" className={classes.main} container>
        {isSurveyLoaded && (
          <>
            {isObjectEmpty(fillSurvey) ? (
              this.renderExpiredSurvey()
            ) : (
              <Grid className={classes.content} item xs={12}>
                <FillReviewFlow
                  translations={translations}
                  surveyStep={surveyStep}
                  survey={fillSurvey}
                  review={review}
                  steps={prepareSurveySteps(review, !!fillSurvey.questionnaire)}
                  activeStep={reviewStep}
                  selectedSubject={selectedSubject}
                  feedbackErrors={feedbackErrors}
                  answerReplyErrors={answerReplyErrors}
                  isInlineSurvey={isInlineSurvey}
                  isPreview={review.isSubmitted}
                  isAddCommentDialogOpened={isAddCommentDialogOpened}
                  hasAddCommentButton={hasAddCommentButton}
                  onCloseInlineSurvey={this.handleCloseInlineSurvey}
                  onSetFeedbackErrors={this.handleFeedbackErrors}
                  onSetFeedbackComments={this.handleSetComments}
                  onStepChange={this.handleStepChange}
                  onAttributeQuestionAnswerSelect={
                    this.handleAttributeQuestionAnswerSelect
                  }
                  onAttributeQuestionAnswerChange={
                    this.handleAttributeQuestionAnswerChange
                  }
                  onQuestionnaireQuestionOptionSelect={
                    this.handleQuestionnaireQuestionOptionSelect
                  }
                  onQuestionnaireQuestionAnswerChange={
                    this.handleQuestionnaireQuestionAnswerChange
                  }
                  onSetAnswerReplyErrors={this.handleAnswerReplyErrors}
                  onSubmit={this.handleSubmitReview}
                  onGetStarted={() => this.handleGetReview(activeReviewIndex)}
                  onAddComment={this.handleOpenAddCommentDialog}
                  onSaveComment={this.handleSaveComment}
                />
              </Grid>
            )}
          </>
        )}
        <AlertDialog
          translations={
            surveyStep + 1 < fillSurvey.totalReviews
              ? translations.submitMultipleDialog
              : translations.submitDialog
          }
          isOpened={isSubmitOpened}
          onClose={this.toggleSubmitDialog}
          onConfirm={this.handleFinishReview}
        />
      </Grid>
    );
  }
}

FillSurveyPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  trackingToken: PropTypes.string.isRequired,
  isSurveyLoaded: PropTypes.bool.isRequired,
  fillSurvey: PropTypes.shape({}).isRequired,
  review: PropTypes.shape({}).isRequired,
  surveyStep: PropTypes.number.isRequired,
  reviewStep: PropTypes.number.isRequired,
  isInlineSurvey: PropTypes.bool.isRequired,
  setIsInlineSurvey: PropTypes.func.isRequired,
  getSurveyReview: PropTypes.func.isRequired,
  setAttributeAnswerResult: PropTypes.func.isRequired,
  setQuestionnaireAnswerResult: PropTypes.func.isRequired,
  setAnswerTextResult: PropTypes.func.isRequired,
  setAnswerCommentResult: PropTypes.func.isRequired,
  setReviewStep: PropTypes.func.isRequired,
  setComments: PropTypes.func.isRequired,
  submitReview: PropTypes.func.isRequired,
  clearFillSurvey: PropTypes.func.isRequired,
};

export default withStyles(styles)(FillSurveyPage);
